<script setup lang="ts">
import { ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

/**
 * Reason prompt shared by the payroll reject/reverse actions.
 *
 * The payroll API requires `rejection_reason` / `reversal_reason` on those
 * endpoints, so the reason is mandatory and the caller cannot submit an empty
 * string to the backend.
 */
const props = withDefaults(
  defineProps<{
    open: boolean
    title: string
    message?: string
    label?: string
    confirmLabel?: string
    busy?: boolean
  }>(),
  {
    message: '',
    label: '',
    confirmLabel: '',
    busy: false,
  },
)

const emit = defineEmits<{
  close: []
  confirm: [reason: string]
}>()

const { t } = useI18n()
const reason = ref('')
const localError = ref('')

function submit() {
  const value = reason.value.trim()
  if (!value) {
    localError.value = t('finance.common.requiredField', { field: t('finance.common.reason') })
    return
  }
  emit('confirm', value)
}

watch(
  () => props.open,
  (isOpen) => {
    if (!isOpen) return
    reason.value = ''
    localError.value = ''
  },
)
</script>

<template>
  <div v-if="open" class="modal-overlay" @click.self="emit('close')">
    <div class="modal" role="dialog" aria-modal="true">
      <h3>{{ title }}</h3>
      <p v-if="message" class="empty-inline">{{ message }}</p>
      <div class="field">
        <label for="payroll-reason">{{ label || t('finance.common.reason') }}</label>
        <textarea id="payroll-reason" v-model="reason" rows="3" />
        <small v-if="localError" class="error-inline">{{ localError }}</small>
      </div>
      <div class="modal-actions">
        <button class="btn-secondary" type="button" :disabled="busy" @click="emit('close')">
          {{ t('finance.common.cancel') }}
        </button>
        <button class="btn-primary" type="button" :disabled="busy" @click="submit">
          {{ confirmLabel || t('finance.common.save') }}
        </button>
      </div>
    </div>
  </div>
</template>
